// React
import { ChangeEvent, VFC } from "react";
// API
import { GetBusinessUnits_businessUnits_deliveryAddress } from "api/BusinessUnits/types/GetBusinessUnits";
// Utils
import { BU_TYPES } from "trading/constants";
import { getBUFromLocalStorage } from "helpers/storage";
import { useGetOrganisationBusinessUnits } from "hooks";
// Types
import { MarkOffFarmValues } from "components/MyLivestock/Animals/AnimalBulkActions/ConfirmMarkOffFarmModal";
// Components
import { Field, InputSelect, LoadingOverlay } from "components";
// Resources
import styles from "./BUWithLocationsSelect.module.scss";

export interface LocationOptions extends GetBusinessUnits_businessUnits_deliveryAddress {
  farmName: string;
  farmId: string;
}

interface BUWithLocationSelectProps {
  disabled?: boolean;
  onLocationChange: (values: MarkOffFarmValues) => void;
  narrowBySpeciesId?: string;
  value: string;
}

export const BUWithLocationSelect: VFC<BUWithLocationSelectProps> = ({
  disabled,
  narrowBySpeciesId,
  onLocationChange,
  value,
}) => {
  const { data: businessUnits, loading } = useGetOrganisationBusinessUnits();

  const currentBU = businessUnits.find((item) => (item ? +item.id === +getBUFromLocalStorage() : false));

  const busInOrganisation = businessUnits.filter((item) => {
    const isCurrentOrganisation = currentBU && item?.organisation?.id === currentBU.organisation?.id;
    const isFarmer = item?.businessType?.slug === BU_TYPES.farmer;
    const isCurrentBU = currentBU && item?.id === currentBU.id;
    const isCorrectSpecies = narrowBySpeciesId
      ? !!item?.farmAnimalTypes.find((animalType) => animalType.id === narrowBySpeciesId)
      : true;

    return item ? isCurrentOrganisation && isFarmer && !isCurrentBU && isCorrectSpecies : false;
  });

  const placeholder = loading
    ? "Loading locations"
    : busInOrganisation.length < 1
    ? "No suitable locations"
    : "Select new location";
  const isDisabled = disabled || loading;

  const deliveryAddresses: {
    key: string;
    value: string;
    label: string;
    data: LocationOptions;
  }[] = busInOrganisation.reduce((accumulator, currentValue) => {
    const addresses =
      currentValue?.deliveryAddress.map((item) => ({
        key: item.id,
        value: item.id,
        label: `${currentValue?.name}, ${item.unitName}, CPH: ${item.locationId}`,
        data: { ...item, farmName: currentValue.name, farmId: currentValue.id },
      })) || [];
    return [...accumulator, ...addresses];
  }, [] as { key: string; value: string; label: string; data: LocationOptions }[]);

  const handleChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const locationData = deliveryAddresses.find((item) => item.value === e.target.value)?.data;
    const values = {
      location: locationData?.id || "",
      destinationCPHNumber: locationData?.locationId || "",
      destinationPostcode: locationData?.postcode || "",
      destinationAddress: locationData?.addressLine1 || "",
      destinationName: locationData?.farmName || "",
      destinationId: locationData?.farmId || "",
    };
    onLocationChange(values);
  };

  return (
    <Field label="Move to one of your holdings or farms" labelFor="location">
      <div className={styles.organisation_business_unit_select}>
        <InputSelect
          disabled={isDisabled}
          isClearable={true}
          name="location"
          onChange={handleChange}
          options={deliveryAddresses}
          placeholder={placeholder}
          value={value}
        />

        {loading && <LoadingOverlay />}
      </div>
    </Field>
  );
};
